"use client"

import * as React from "react"
import { Menu as MenuPrimitive } from "@base-ui/react/menu"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { ChevronRightIcon, CheckIcon } from "lucide-react"

const dropdownMenuItemVariants = cva(
  // Base styles - shared across all variants
  [
    "group/dropdown-menu-item relative flex cursor-default items-center gap-2",
    "rounded-lg px-2 py-1.5 text-sm",
    "outline-hidden select-none",
    "transition-all duration-150",
    // Mobile touch optimization
    "touch-manipulation",
    "active:bg-muted/80 dark:active:bg-slate-600/50",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  ].join(" "),
  {
    variants: {
      variant: {
        default: [
          "text-foreground",
          "hover:bg-muted/80",
          "focus:bg-muted/80 dark:focus:bg-slate-700/50",
          "focus:text-foreground",
        ].join(" "),
        destructive: [
          "text-error-text",
          "hover:bg-error-soft dark:hover:bg-red-900/20",
          "focus:bg-error-soft dark:focus:bg-red-900/20",
        ].join(" "),
        ghost: [
          "text-foreground/70",
          "hover:bg-transparent hover:text-foreground",
          "focus:bg-transparent focus:text-foreground",
        ].join(" "),
      },
      size: {
        default: "py-2",
        sm: "py-1.5 text-xs",
        lg: "py-2.5 text-base",
      },
      inset: {
        true: "pl-6",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      inset: false,
    },
  }
)

type DropdownMenuItemVariants = VariantProps<typeof dropdownMenuItemVariants>

const dropdownMenuContentVariants = cva(
  [
    "z-50 min-w-[180px] origin-(--transform-origin)",
    "rounded-xl p-1",
    "backdrop-blur-xl",
    "text-popover-foreground",
    "overflow-x-hidden overflow-y-auto max-h-(--available-height)",
    "duration-200 outline-none",
    // Animation states
    "data-[side=bottom]:slide-in-from-top-2",
    "data-[side=inline-end]:slide-in-from-left-2",
    "data-[side=inline-start]:slide-in-from-right-2",
    "data-[side=left]:slide-in-from-right-2",
    "data-[side=top]:slide-in-from-bottom-2",
    "data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95",
    "data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
  ].join(" "),
  {
    variants: {
      variant: {
        default: [
          "bg-card",
          "border border-surface",
          "shadow-card",
        ].join(" "),
        solid: [
          "bg-card",
          "border border-border",
          "shadow-elev",
        ].join(" "),
        subtle: [
          "bg-overlay/60",
          "border border-surface",
          "shadow-card",
        ].join(" "),
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

type DropdownMenuContentVariants = VariantProps<typeof dropdownMenuContentVariants>

const dropdownMenuLabelVariants = cva(
  [
    "px-2 py-1.5 text-xs font-semibold uppercase tracking-wider",
  ].join(" "),
  {
    variants: {
      variant: {
        default: "text-muted-foreground dark:text-muted-foreground",
        muted: "text-muted-foreground",
        accent: "text-primary dark:text-primary"
      },
      inset: {
        true: "pl-6",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      inset: false
    }
  }
)

type DropdownMenuLabelVariants = VariantProps<typeof dropdownMenuLabelVariants>

const dropdownMenuSubTriggerVariants = cva(
  [
    "flex cursor-default items-center gap-2",
    "rounded-lg px-2 py-1.5 text-sm",
    "outline-hidden select-none",
    "transition-all duration-150",
    "hover:bg-muted/80",
    "focus:bg-muted/80 dark:focus:bg-slate-700/50",
    "data-popup-open:bg-muted/80 dark:data-popup-open:bg-slate-700/50",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
  ].join(" "),
  {
    variants: {
      inset: {
        true: "pl-6",
        false: "",
      },
    },
    defaultVariants: {
      inset: false
    }
  }
)

type DropdownMenuSubTriggerVariants = VariantProps<typeof dropdownMenuSubTriggerVariants>

const dropdownMenuCheckboxItemVariants = cva(
  [
    "relative flex cursor-default items-center gap-2",
    "rounded-lg px-2 py-1.5 pr-6 text-sm",
    "outline-hidden select-none",
    "transition-all duration-150",
    "hover:bg-muted/80",
    "focus:bg-muted/80 dark:focus:bg-slate-700/50",
    "data-disabled:pointer-events-none data-disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
  ].join(" "),
  {
    variants: {
      inset: {
        true: "pl-6",
        false: "",
      },
    },
    defaultVariants: {
      inset: false
    }
  }
)

type DropdownMenuCheckboxItemVariants = VariantProps<typeof dropdownMenuCheckboxItemVariants>

function DropdownMenu({ ...props }: MenuPrimitive.Root.Props) {
  return <MenuPrimitive.Root data-slot="dropdown-menu" {...props} />
}

function DropdownMenuPortal({ ...props }: MenuPrimitive.Portal.Props) {
  return <MenuPrimitive.Portal data-slot="dropdown-menu-portal" {...props} />
}

function DropdownMenuTrigger({ ...props }: MenuPrimitive.Trigger.Props) {
  return <MenuPrimitive.Trigger data-slot="dropdown-menu-trigger" {...props} />
}

function DropdownMenuContent({
  align = "start",
  alignOffset = 0,
  side = "bottom",
  sideOffset = 4,
  variant = "default",
  className,
  ...props
}: MenuPrimitive.Popup.Props &
  Pick<
    MenuPrimitive.Positioner.Props,
    "align" | "alignOffset" | "side" | "sideOffset"
  > &
  DropdownMenuContentVariants) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Positioner
        className="isolate z-50 outline-none"
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
      >
        <MenuPrimitive.Popup
          data-slot="dropdown-menu-content"
          className={cn(
            dropdownMenuContentVariants({ variant }),
            // Mobile scroll optimization
            "overscroll-contain",
            "[&::-webkit-overflow-scrolling:touch]",
            className
          )}
          {...props}
        />
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  )
}

function DropdownMenuGroup({ ...props }: MenuPrimitive.Group.Props) {
  return <MenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />
}

function DropdownMenuLabel({
  className,
  variant = "default",
  inset,
  ...props
}: MenuPrimitive.GroupLabel.Props & DropdownMenuLabelVariants) {
  return (
    <MenuPrimitive.GroupLabel
      data-slot="dropdown-menu-label"
      data-inset={inset}
      className={cn(
        dropdownMenuLabelVariants({ variant, inset }),
        className
      )}
      {...props}
    />
  )
}

function DropdownMenuItem({
  className,
  variant = "default",
  size = "default",
  inset,
  render,
  ...props
}: MenuPrimitive.Item.Props & DropdownMenuItemVariants & {
  render?: React.ReactElement<Record<string, unknown>>
}) {
  return (
    <MenuPrimitive.Item
      data-slot="dropdown-menu-item"
      data-inset={inset}
      data-variant={variant}
      className={cn(
        dropdownMenuItemVariants({ variant, size, inset }),
        "data-disabled:pointer-events-none data-disabled:opacity-50",
        className
      )}
      render={render}
      {...props}
    />
  )
}

function DropdownMenuSub({ ...props }: MenuPrimitive.SubmenuRoot.Props) {
  return <MenuPrimitive.SubmenuRoot data-slot="dropdown-menu-sub" {...props} />
}

function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: MenuPrimitive.SubmenuTrigger.Props & DropdownMenuSubTriggerVariants) {
  return (
    <MenuPrimitive.SubmenuTrigger
      data-slot="dropdown-menu-sub-trigger"
      data-inset={inset}
      className={cn(
        dropdownMenuSubTriggerVariants({ inset }),
        className
      )}
      {...props}
    >
      {children}
      <ChevronRightIcon className="ml-auto h-4 w-4 text-muted-foreground" />
    </MenuPrimitive.SubmenuTrigger>
  )
}

function DropdownMenuSubContent({
  align = "start",
  alignOffset = -3,
  side = "right",
  sideOffset = 0,
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuContent>) {
  return (
    <DropdownMenuContent
      data-slot="dropdown-menu-sub-content"
      className={cn(
        "min-w-[160px]",
        className
      )}
      align={align}
      alignOffset={alignOffset}
      side={side}
      sideOffset={sideOffset}
      {...props}
    />
  )
}

function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  inset,
  ...props
}: MenuPrimitive.CheckboxItem.Props & DropdownMenuCheckboxItemVariants) {
  return (
    <MenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      data-inset={inset}
      className={cn(
        dropdownMenuCheckboxItemVariants({ inset }),
        className
      )}
      checked={checked}
      {...props}
    >
      {children}
      <span
        className="pointer-events-none absolute right-3 flex items-center justify-center"
        data-slot="dropdown-menu-checkbox-item-indicator"
      >
        <MenuPrimitive.CheckboxItemIndicator>
          <CheckIcon className="h-4 w-4 text-primary" />
        </MenuPrimitive.CheckboxItemIndicator>
      </span>
    </MenuPrimitive.CheckboxItem>
  )
}

function DropdownMenuRadioGroup({ ...props }: MenuPrimitive.RadioGroup.Props) {
  return (
    <MenuPrimitive.RadioGroup
      data-slot="dropdown-menu-radio-group"
      {...props}
    />
  )
}

function DropdownMenuRadioItem({
  className,
  children,
  inset,
  ...props
}: MenuPrimitive.RadioItem.Props & DropdownMenuCheckboxItemVariants) {
  return (
    <MenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      data-inset={inset}
      className={cn(
        dropdownMenuCheckboxItemVariants({ inset }),
        className
      )}
      {...props}
    >
      {children}
      <span
        className="pointer-events-none absolute right-3 flex items-center justify-center"
        data-slot="dropdown-menu-radio-item-indicator"
      >
        <MenuPrimitive.RadioItemIndicator>
          <CheckIcon className="h-4 w-4 text-primary" />
        </MenuPrimitive.RadioItemIndicator>
      </span>
    </MenuPrimitive.RadioItem>
  )
}

const dropdownMenuSeparatorVariants = cva(
  "-mx-1.5 my-1.5 h-px",
  {
    variants: {
      variant: {
        default: "bg-muted/50",
        strong: "bg-muted/70 dark:bg-slate-600/70",
        subtle: "bg-muted/30",
      },
    },
    defaultVariants: {
      variant: "default"
    }
  }
)

type DropdownMenuSeparatorVariants = VariantProps<typeof dropdownMenuSeparatorVariants>

function DropdownMenuSeparator({
  className,
  variant = "default",
  ...props
}: MenuPrimitive.Separator.Props & DropdownMenuSeparatorVariants) {
  return (
    <MenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn(
        dropdownMenuSeparatorVariants({ variant }),
        className
      )}
      {...props}
    />
  )
}

function DropdownMenuShortcut({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={cn(
        "ml-auto text-xs tracking-widest",
        "text-muted-foreground/70",
        "group-focus/dropdown-menu-item:text-muted-foreground",
        "group-data-[variant=destructive]/dropdown-menu-item:text-red-400/70",
        "transition-colors duration-150",
        className
      )}
      {...props}
    />
  )
}

// Type exports for consumers
export type {
  DropdownMenuItemVariants,
  DropdownMenuContentVariants,
  DropdownMenuLabelVariants,
  DropdownMenuSeparatorVariants,
  DropdownMenuSubTriggerVariants,
  DropdownMenuCheckboxItemVariants,
}

// Variant exports for advanced usage
export {
  dropdownMenuItemVariants,
  dropdownMenuContentVariants,
  dropdownMenuLabelVariants,
  dropdownMenuSeparatorVariants,
  dropdownMenuSubTriggerVariants,
  dropdownMenuCheckboxItemVariants,
}

// Component exports
export {
  DropdownMenu,
  DropdownMenuPortal,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
}
